/**
 * DataHealthFicha — contenido COMPARTIDO de la ficha de detalle de un ítem de Data Health.
 *
 * Es el cuerpo de las 3 tabs (Resumen / Enriquecimiento / Historial); NO arma el wrapper
 * (eso lo hace el caller con ItemDetailSplitPane [Ocultos, modo 2] o ItemDetailSideDrawer
 * [Cola, modo 3]). Presunción validada con Gabriel (2026-08-07): la ficha es la MISMA para
 * Ocultos y Cola; solo cambian el wrapper y las acciones del footer.
 *
 * Spec Gabriel:
 *  - Header: título = SOLO id_catalogo (lo pone el caller).
 *  - RESUMEN: panel superior (foto + campos core etiquetados) + tabla Campo | Visible | Contenido
 *    (todos los campos de COLUMNAS, con el valor que ve el usuario en Publicaciones).
 *  - ENRIQUECIMIENTO: panel "último enriquecimiento" + tabla Campo | Visible | Cont. TOMA | Cont. CATALOG.
 *  - HISTORIAL: timeline (presentacional; el caller pasa los eventos ya normalizados).
 *
 * Los campos NO se hardcodean: salen de `resolveColumns(columnConfig, item)`.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  Image as ImageIcon,
  Info,
  Sparkles,
  History as HistoryIcon,
  Loader2,
  Filter as FilterIcon,
  Sparkle,
  RefreshCw,
  Pencil,
  Eye,
  EyeOff,
  ShieldAlert,
  PackageSearch,
  Flag,
  Link2,
  Clock,
} from 'lucide-react';
import type { DataHealthItem } from '../../../types/dataHealth';
import type { DetailTab } from '../../common/ItemDetail';
import { resolveColumns, type ResolvedColumn } from './columnResolver';
import { useColumnConfig } from './useColumnConfig';
import {
  formatDateTime,
  formatPrice,
  ConfidenceBadge,
} from '../shared/inspectorTableHelpers';
import ImageModal from '../../ImageModal';

export type FichaTab = 'resumen' | 'enriquecimiento' | 'historial';

export const DATA_HEALTH_FICHA_TABS: DetailTab[] = [
  { id: 'resumen', label: 'Resumen', icon: Info },
  { id: 'enriquecimiento', label: 'Enriquecimiento', icon: Sparkles },
  { id: 'historial', label: 'Historial', icon: HistoryIcon },
];

// ----------------------------------------------------------------------------
// Historial — evento normalizado (Ocultos y Cola lo adaptan a esta forma).
// ----------------------------------------------------------------------------

/** Tipo semántico del evento — determina el ícono del timeline (A5). */
export type FichaEventKind =
  | 'created'
  | 'recataloged'
  | 'manual_edit'
  | 'visibility_shown'
  | 'visibility_hidden'
  | 'auto_hide'
  | 'first_seen'
  | 'outlier'
  | 'url'
  | 'feedback'
  | 'generic';

export interface FichaHistoryEvent {
  id: string;
  label: string;
  actor: string;
  at: string; // ISO
  detail?: React.ReactNode;
  /** Tipo semántico → ícono del timeline. Default 'generic'. */
  kind?: FichaEventKind;
}

// ----------------------------------------------------------------------------
// Sub-componentes de UI
// ----------------------------------------------------------------------------

const VisibleBadge: React.FC<{ visible: boolean }> = ({ visible }) =>
  visible ? (
    <span className="inline-block text-[10px] bg-green-100 text-green-700 rounded px-1.5 py-0.5">Sí</span>
  ) : (
    <span className="inline-block text-[10px] bg-gray-200 text-gray-500 rounded px-1.5 py-0.5">No</span>
  );

const Cell: React.FC<{ value: string }> = ({ value }) =>
  value === '—' ? <span className="text-gray-300">—</span> : <span>{value}</span>;

const LabeledField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex gap-1.5">
    <span className="text-gray-500 flex-shrink-0">{label}:</span>
    <span className="text-gray-900 font-medium min-w-0 break-words">{children}</span>
  </div>
);

// ----------------------------------------------------------------------------
// RESUMEN
// ----------------------------------------------------------------------------

const ResumenView: React.FC<{
  item: DataHealthItem;
  columns: ResolvedColumn[];
  loading: boolean;
  filter: VisibleFilterState;
  onFilterChange: (next: VisibleFilterState) => void;
}> = ({ item, columns, loading, filter, onFilterChange }) => {
  // A2: título ocupa el 100% del ancho arriba; debajo, imagen a la IZQUIERDA
  // (zoom-on-click via ImageModal) + campos a la DERECHA.
  const [zoomOpen, setZoomOpen] = useState(false);
  const hasImage = !!item.imagen;

  return (
    <div className="space-y-4 text-[11px]">
      {/* Panel superior */}
      <div className="border border-gray-200 rounded p-3">
        {/* Título full-width */}
        <div className="text-sm font-bold text-gray-900 leading-snug break-words mb-3">
          {item.title || <span className="text-gray-300 font-normal">Sin título</span>}
        </div>
        {/* Imagen (izq, zoom-on-click) + campos (der) */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => hasImage && setZoomOpen(true)}
            disabled={!hasImage}
            title={hasImage ? 'Ampliar imagen' : 'Sin imagen'}
            className={`w-32 h-32 bg-gray-50 border border-gray-200 rounded flex items-center justify-center flex-shrink-0 overflow-hidden ${
              hasImage ? 'cursor-zoom-in hover:border-blue-300' : 'cursor-default'
            }`}
          >
            {hasImage ? (
              // eslint-disable-next-line jsx-a11y/img-redundant-alt
              <img src={item.imagen || undefined} alt="imagen producto" className="object-contain max-w-full max-h-full" />
            ) : (
              <ImageIcon className="w-8 h-8 text-gray-300" />
            )}
          </button>
          <div className="flex-1 min-w-0 space-y-1.5">
            <LabeledField label="Origen">{item.sitio || '—'}</LabeledField>
            <LabeledField label="Precio">{formatPrice(item.sale_price)}</LabeledField>
            <LabeledField label="Confianza">
              <ConfidenceBadge value={item.confidence_score} />
            </LabeledField>
            <LabeledField label="Estado">
              {item.visibility === 0 ? (
                <span className="inline-block text-[10px] bg-gray-200 text-gray-700 rounded px-1.5 py-0.5">Oculto</span>
              ) : (
                <span className="inline-block text-[10px] bg-green-100 text-green-700 rounded px-1.5 py-0.5">Visible</span>
              )}
            </LabeledField>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-100 flex justify-end">
          {item.url ? (
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-2.5 py-1 hover:bg-blue-50 font-medium"
            >
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              Ver listing
            </a>
          ) : (
            <span
              title="Este ítem no tiene URL de origen"
              className="inline-flex items-center gap-1 text-gray-300 border border-gray-200 rounded px-2.5 py-1 cursor-not-allowed"
            >
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              Ver listing
            </span>
          )}
        </div>
      </div>

      {/* Tabla: todos los campos de COLUMNAS con lo que ve el usuario en Publicaciones */}
      <FieldsTable
        loading={loading}
        columns={columns}
        filter={filter}
        onFilterChange={onFilterChange}
        headers={['Campo', 'Contenido', 'Visible']}
        renderRow={(c) => (
          <>
            <td className="py-1 text-gray-900 font-medium truncate" title={c.display}>
              <Cell value={c.display} />
              {c.manual && (
                <span className="ml-1 text-[9px] uppercase bg-purple-100 text-purple-700 rounded px-1 py-0.5">
                  manual
                </span>
              )}
            </td>
            <td className="py-1 pl-2 text-right">
              <VisibleBadge visible={c.visible} />
            </td>
          </>
        )}
      />

      {/* Zoom-on-click de la imagen (reusa ImageModal de Publicaciones) */}
      {hasImage && (
        <ImageModal
          isOpen={zoomOpen}
          onClose={() => setZoomOpen(false)}
          imageUrl={item.imagen as string}
          productTitle={item.title || undefined}
          productUrl={item.url || undefined}
          siteName={item.sitio || undefined}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// ENRIQUECIMIENTO
// ----------------------------------------------------------------------------

const EnriquecimientoView: React.FC<{
  item: DataHealthItem;
  columns: ResolvedColumn[];
  loading: boolean;
  filter: VisibleFilterState;
  onFilterChange: (next: VisibleFilterState) => void;
}> = ({ item, columns, loading, filter, onFilterChange }) => {
  const reasoning = item.ai_reasoning != null ? String(item.ai_reasoning) : '';
  return (
    <div className="space-y-4 text-[11px]">
      {/* Panel: último enriquecimiento */}
      <div className="bg-gray-50 border border-gray-200 rounded p-2.5 space-y-1">
        <h5 className="text-[10px] uppercase font-semibold text-gray-500 tracking-wider mb-1">
          Último enriquecimiento
        </h5>
        <div className="grid grid-cols-2 gap-y-1">
          <span className="text-gray-500">Catalogado at:</span>
          <span className="tabular-nums">{formatDateTime(item.last_catalog_at)}</span>
          <span className="text-gray-500">Confianza:</span>
          <span>
            <ConfidenceBadge value={item.confidence_score} />
          </span>
          {item.last_run_id && (
            <>
              <span className="text-gray-500">Run ID:</span>
              <span className="font-mono text-[10px] truncate" title={item.last_run_id}>
                {item.last_run_id.slice(0, 12)}…
              </span>
            </>
          )}
        </div>
        {reasoning && (
          <div className="pt-1">
            <div className="text-[10px] text-gray-500 uppercase mb-0.5">Reasoning LLM</div>
            <div className="text-gray-700 bg-white border border-gray-200 rounded p-2 max-h-32 overflow-auto">
              {reasoning}
            </div>
          </div>
        )}
      </div>

      {/* Tabla: Campo | Cont. TOMA | Cont. CATALOG | Visible */}
      <FieldsTable
        loading={loading}
        columns={columns}
        filter={filter}
        onFilterChange={onFilterChange}
        headers={['Campo', 'Cont. TOMA', 'Cont. CATALOG', 'Visible']}
        renderRow={(c) => (
          <>
            <td className="py-1 text-gray-600 truncate" title={c.tomaDisplay}>
              <Cell value={c.tomaDisplay} />
            </td>
            <td className="py-1 text-gray-900 font-medium truncate" title={c.catalogDisplay}>
              <Cell value={c.catalogDisplay} />
              {c.manual && (
                <span className="ml-1 text-[9px] uppercase bg-purple-100 text-purple-700 rounded px-1 py-0.5">
                  manual
                </span>
              )}
            </td>
            <td className="py-1 pl-2 text-right">
              <VisibleBadge visible={c.visible} />
            </td>
          </>
        )}
      />
    </div>
  );
};

// Quick filter tipo Excel para la columna "Visible": Todos / Sí / No con checks
// individuales. Vive en la cabecera; al filtrar, la tabla oculta las filas que no
// matchean. Ambos checks marcados = "Todos".
const VisibleHeaderFilter: React.FC<{
  showYes: boolean;
  showNo: boolean;
  onChange: (next: { showYes: boolean; showNo: boolean }) => void;
}> = ({ showYes, showNo, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = !(showYes && showNo); // hay filtro aplicado (no "Todos")

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex items-center gap-1 justify-end">
      <span>Visible</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Filtrar por visibilidad"
        className={`p-0.5 rounded hover:bg-gray-100 ${active ? 'text-blue-600' : 'text-gray-400'}`}
      >
        <FilterIcon className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-30 bg-white border border-gray-200 rounded shadow-lg py-1 w-28 text-left">
          <button
            type="button"
            onClick={() => onChange({ showYes: true, showNo: true })}
            className="w-full text-left px-2 py-1 text-[11px] hover:bg-gray-50 text-gray-700 border-b border-gray-100"
          >
            Todos
          </button>
          <label className="flex items-center gap-1.5 px-2 py-1 text-[11px] hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={showYes}
              onChange={(e) => onChange({ showYes: e.target.checked, showNo })}
              className="w-3 h-3"
            />
            Sí
          </label>
          <label className="flex items-center gap-1.5 px-2 py-1 text-[11px] hover:bg-gray-50 cursor-pointer">
            <input
              type="checkbox"
              checked={showNo}
              onChange={(e) => onChange({ showYes, showNo: e.target.checked })}
              className="w-3 h-3"
            />
            No
          </label>
        </div>
      )}
    </div>
  );
};

/** Estado del filtro de la columna VISIBLE. Se levanta al padre (DataHealthFichaBody)
 *  para que PERSISTA al cambiar entre tabs Resumen/Enriquecimiento (A3). */
export interface VisibleFilterState {
  showYes: boolean;
  showNo: boolean;
}

// Anchos FIJOS por número de columnas de datos (A4: table-layout:fixed → los anchos
// NO varían por contenido). Primera col = "Campo" (label), última = "Visible".
// El resto (contenido) reparte el sobrante. Se elige por la cantidad de headers.
function fixedColWidths(headerCount: number): string[] {
  // headerCount incluye "Campo" (primero) y "Visible" (último).
  if (headerCount === 3) {
    // Resumen: Campo | Contenido | Visible
    return ['9rem', 'auto', '4.5rem'];
  }
  // Enriquecimiento: Campo | Cont. TOMA | Cont. CATALOG | Visible
  return ['9rem', 'auto', 'auto', '4.5rem'];
}

// Tabla genérica de campos (comparte cabecera Campo + columnas variables).
// A3: el estado del filtro Visible lo controla el PADRE (persiste entre tabs).
// A4: anchos FIJOS con table-layout:fixed + <colgroup>.
const FieldsTable: React.FC<{
  loading: boolean;
  columns: ResolvedColumn[];
  headers: string[];
  filter: VisibleFilterState;
  onFilterChange: (next: VisibleFilterState) => void;
  renderRow: (c: ResolvedColumn) => React.ReactNode;
}> = ({ loading, columns, headers, filter, onFilterChange, renderRow }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando columnas…
      </div>
    );
  }
  if (columns.length === 0) {
    return <div className="text-gray-400 italic py-2">No hay columnas configuradas para este cliente.</div>;
  }

  const { showYes, showNo } = filter;
  const visibleRows = columns.filter((c) => (c.visible ? showYes : showNo));
  const widths = fixedColWidths(headers.length);

  return (
    <table className="w-full table-fixed">
      <colgroup>
        {widths.map((w, i) => (
          <col key={i} style={w === 'auto' ? undefined : { width: w }} />
        ))}
      </colgroup>
      <thead>
        <tr className="border-b border-gray-200 text-gray-500">
          {headers.map((h) => (
            <th key={h} className={`py-1 font-medium ${h === 'Visible' ? 'text-right pl-2' : 'text-left'}`}>
              {h === 'Visible' ? (
                <VisibleHeaderFilter
                  showYes={showYes}
                  showNo={showNo}
                  onChange={onFilterChange}
                />
              ) : (
                h
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {visibleRows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="py-2 text-center text-gray-400 italic">
              Ningún campo coincide con el filtro de visibilidad.
            </td>
          </tr>
        ) : (
          visibleRows.map((c) => (
            <tr key={`${c.group}:${c.key}`} className="border-b border-gray-100">
              <td className="py-1 text-gray-500 truncate" title={c.label}>{c.label}</td>
              {renderRow(c)}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
};

// ----------------------------------------------------------------------------
// HISTORIAL (presentacional)
// ----------------------------------------------------------------------------

// A5: mapa tipo de evento → ícono + color del nodo del timeline.
const EVENT_ICON: Record<FichaEventKind, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  created: { icon: Sparkle, color: 'bg-emerald-100 text-emerald-600 border-emerald-200' },
  recataloged: { icon: RefreshCw, color: 'bg-blue-100 text-blue-600 border-blue-200' },
  manual_edit: { icon: Pencil, color: 'bg-purple-100 text-purple-600 border-purple-200' },
  visibility_shown: { icon: Eye, color: 'bg-green-100 text-green-600 border-green-200' },
  visibility_hidden: { icon: EyeOff, color: 'bg-gray-100 text-gray-500 border-gray-200' },
  auto_hide: { icon: ShieldAlert, color: 'bg-amber-100 text-amber-600 border-amber-200' },
  first_seen: { icon: PackageSearch, color: 'bg-sky-100 text-sky-600 border-sky-200' },
  outlier: { icon: Flag, color: 'bg-orange-100 text-orange-600 border-orange-200' },
  url: { icon: Link2, color: 'bg-indigo-100 text-indigo-600 border-indigo-200' },
  feedback: { icon: HistoryIcon, color: 'bg-blue-100 text-blue-600 border-blue-200' },
  generic: { icon: Clock, color: 'bg-gray-100 text-gray-500 border-gray-200' },
};

/**
 * A5 — Timeline vertical del historial del ítem. Cada evento es un nodo con:
 *  - ícono por tipo de evento (kind),
 *  - label + actor,
 *  - fecha DD/MM/YYYY HH:mm (es-AR, 24h, tabular-nums),
 *  - detalle/diffs before→after (los provee el caller como `detail`).
 * Línea vertical continua conecta los nodos.
 */
export const HistorialView: React.FC<{
  events: FichaHistoryEvent[] | null;
  loading: boolean;
  error?: string | null;
  backendDown?: boolean;
}> = ({ events, loading, error, backendDown }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando historial…
      </div>
    );
  }
  if (backendDown) {
    return (
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
        Historial todavía no disponible — endpoint backend pendiente de deploy.
      </div>
    );
  }
  if (error) {
    return <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>;
  }
  if (!events || events.length === 0) {
    return <div className="text-xs text-gray-500 italic">Sin eventos registrados.</div>;
  }
  return (
    <ol className="relative ml-2 text-[11px]">
      {events.map((ev, i) => {
        const meta = EVENT_ICON[ev.kind ?? 'generic'] ?? EVENT_ICON.generic;
        const Icon = meta.icon;
        const isLast = i === events.length - 1;
        return (
          <li key={ev.id} className="relative pl-6 pb-4 last:pb-0">
            {/* Línea vertical entre nodos */}
            {!isLast && (
              <span className="absolute left-[9px] top-5 bottom-0 w-px bg-gray-200" aria-hidden />
            )}
            {/* Nodo con ícono */}
            <span
              className={`absolute left-0 top-0.5 w-[19px] h-[19px] rounded-full border flex items-center justify-center ${meta.color}`}
              aria-hidden
            >
              <Icon className="w-2.5 h-2.5" />
            </span>
            {/* Cabecera del evento */}
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="font-semibold text-gray-800">{ev.label}</span>
              {ev.actor && ev.actor !== '—' && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-600">{ev.actor}</span>
                </>
              )}
            </div>
            <div className="text-gray-400 tabular-nums text-[10px] mt-0.5">{formatDateTime(ev.at)}</div>
            {/* Detalle / diffs */}
            {ev.detail && <div className="mt-1">{ev.detail}</div>}
          </li>
        );
      })}
    </ol>
  );
};

// ----------------------------------------------------------------------------
// Ficha completa (Resumen + Enriquecimiento; el caller decide qué tab mostrar).
// ----------------------------------------------------------------------------

interface FichaBodyProps {
  clientId: string;
  item: DataHealthItem;
  tab: FichaTab;
  /** Slot para la tab de historial (cada caller trae su loader). */
  historial: React.ReactNode;
}

/**
 * Cuerpo de la ficha para las tabs de datos (Resumen / Enriquecimiento). Resuelve las
 * columnas desde COLUMNAS con cache por-cliente. La tab Historial se inyecta como slot.
 */
export const DataHealthFichaBody: React.FC<FichaBodyProps> = ({ clientId, item, tab, historial }) => {
  const { config, loading } = useColumnConfig(clientId);
  const columns = React.useMemo(
    () => resolveColumns(config, item as Record<string, unknown>),
    [config, item]
  );

  // A3: el filtro de la columna VISIBLE vive acá (en el body de la ficha), NO dentro de
  // FieldsTable, para que PERSISTA al alternar entre las tabs Resumen y Enriquecimiento
  // (antes se remontaba con cada tab y se perdía).
  const [visibleFilter, setVisibleFilter] = useState<VisibleFilterState>({ showYes: true, showNo: true });

  return (
    <>
      {tab === 'resumen' && (
        <ResumenView
          item={item}
          columns={columns}
          loading={loading}
          filter={visibleFilter}
          onFilterChange={setVisibleFilter}
        />
      )}
      {tab === 'enriquecimiento' && (
        <EnriquecimientoView
          item={item}
          columns={columns}
          loading={loading}
          filter={visibleFilter}
          onFilterChange={setVisibleFilter}
        />
      )}
      {tab === 'historial' && historial}
    </>
  );
};
